import { CalendarOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../api/errors';
import {
  TaxType,
  changeTaxRateBehaviour,
  createTaxRate,
  listTaxRates,
  setTaxRateActive,
  setTaxRateDefault,
  supersedeTaxRate,
  taxTypeNames,
  updateTaxRateDescription,
} from '../api/taxRates';
import type { TaxRate, TaxTypeValue } from '../api/taxRates';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';

/** Dữ liệu của một trang đã nạp xong, kèm khóa nhận dạng lần nạp đó. */
interface LoadedPage {
  readonly key: string;
  readonly items: readonly TaxRate[];
  readonly totalCount: number;
}

/** Giá trị của biểu mẫu khai thuế suất mới. */
interface CreateFormValues {
  readonly code: string;
  readonly name: string;
  readonly taxType: TaxTypeValue;
  readonly ratePercent: number;
  readonly validFrom: Dayjs;
  readonly isValueAddedTaxExempt: boolean;
  readonly isNotDeclarable: boolean;
  readonly generalLedgerAccountCode: string | null;
  readonly legalBasis: string | null;
}

/** Giá trị của biểu mẫu sửa phần mô tả. */
interface DescriptionFormValues {
  readonly name: string;
  readonly generalLedgerAccountCode: string | null;
  readonly legalBasis: string | null;
}

/** Giá trị của biểu mẫu sửa tỷ lệ tại chỗ. */
interface BehaviourFormValues {
  readonly ratePercent: number;
  readonly isValueAddedTaxExempt: boolean;
  readonly isNotDeclarable: boolean;
}

/** Giá trị của biểu mẫu đổi tỷ lệ từ một ngày. */
interface SupersedeFormValues {
  readonly effectiveFrom: Dayjs;
  readonly ratePercent: number;
  readonly isValueAddedTaxExempt: boolean;
  readonly isNotDeclarable: boolean;
  readonly legalBasis: string | null;
}

/** Số bản ghi mỗi trang. Đặt rộng hơn các danh mục khác vì mỗi mã có thể có nhiều dòng. */
const pageSize = 50;

/** Khuôn ngày hiển thị. */
const dateFormat = 'DD/MM/YYYY';

/** Các lựa chọn của ô chọn loại thuế. */
const taxTypeOptions = Object.entries(taxTypeNames).map(([value, label]) => ({
  value: Number(value) as TaxTypeValue,
  label,
}));

/**
 * Trang danh mục thuế suất.
 *
 * Màn hình này khác ba màn hình danh mục trước ở một điểm quyết định: nó hiển thị **chuỗi hiệu lực theo
 * thời gian**. Cùng một mã thuế có nhiều dòng qua các giai đoạn, và đó là thứ phải xem được khi giải
 * trình với cơ quan thuế rằng tỷ lệ nào đã áp dụng trong giai đoạn nào.
 *
 * Vì vậy nút chính không phải "Sửa tỷ lệ" mà là **"Đổi tỷ lệ từ ngày…"**. Cách làm đúng phải là cách
 * dễ nhất: nếu để nút sửa tỷ lệ ngay trên dòng đang dùng thì người dùng sẽ sửa tại chỗ, và lịch sử mất.
 *
 * Ô sửa tỷ lệ tại chỗ chỉ hiện với dòng **chưa** bắt đầu có hiệu lực — lúc đó nó chưa được dùng trên
 * chứng từ nào nên sửa là vô hại. Máy chủ tính sẵn dấu hiệu đó, giao diện không tự suy ra từ ngày.
 */
export function TaxRatePage(): ReactNode {
  const { hasPermission } = useAuth();

  const canManage = hasPermission(PermissionCodes.TaxRateManage);

  const [createForm] = Form.useForm<CreateFormValues>();
  const [descriptionForm] = Form.useForm<DescriptionFormValues>();
  const [behaviourForm] = Form.useForm<BehaviourFormValues>();
  const [supersedeForm] = Form.useForm<SupersedeFormValues>();

  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<TaxTypeValue | null>(null);
  const [onlyEffectiveToday, setOnlyEffectiveToday] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [loaded, setLoaded] = useState<LoadedPage | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingDescription, setEditingDescription] = useState<TaxRate | null>(null);
  const [editingBehaviour, setEditingBehaviour] = useState<TaxRate | null>(null);
  const [supersedingRate, setSupersedingRate] = useState<TaxRate | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const requestKey = useMemo(
    () =>
      JSON.stringify({
        searchTerm,
        typeFilter,
        onlyEffectiveToday,
        includeInactive,
        pageNumber,
        reloadToken,
      }),
    [searchTerm, typeFilter, onlyEffectiveToday, includeInactive, pageNumber, reloadToken],
  );

  const isLoading = loaded?.key !== requestKey && errorMessage === null;

  const reload = useCallback((): void => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const page = await listTaxRates({
          searchTerm,
          ...(typeFilter === null ? {} : { taxType: typeFilter }),

          /*
            Lọc theo ngày hôm nay dùng ngày của máy người dùng, và đó là chấp nhận được ở đây: nó chỉ
            là một bộ lọc để xem, không phải căn cứ tính thuế. Dấu hiệu "đang có hiệu lực" trên từng
            dòng thì do máy chủ tính theo múi giờ nghiệp vụ.
          */
          ...(onlyEffectiveToday ? { effectiveOn: dayjs().format('YYYY-MM-DD') } : {}),
          includeInactive,
          pageNumber,
          pageSize,
        });

        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(null);
        setLoaded({ key: requestKey, items: page.items, totalCount: page.totalCount });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError
            ? error.message
            : 'Không kết nối được tới máy chủ. Kiểm tra lại đường mạng.',
        );
      }
    })();

    return () => {
      controller.abort();
    };
  }, [requestKey, searchTerm, typeFilter, onlyEffectiveToday, includeInactive, pageNumber]);

  const items = useMemo<readonly TaxRate[]>(() => loaded?.items ?? [], [loaded]);
  const totalCount = loaded?.totalCount ?? 0;

  /** Gọi một thao tác và nạp lại danh sách, hiển thị nguyên văn lý do khi máy chủ từ chối. */
  async function run(action: () => Promise<unknown>, successMessage: string): Promise<boolean> {
    setIsSaving(true);

    try {
      await action();
      void message.success(successMessage);
      reload();

      return true;
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không lưu được. Thử lại sau.',
      );

      return false;
    } finally {
      setIsSaving(false);
    }
  }

  function openCreateForm(): void {
    createForm.setFieldsValue({
      code: '',
      name: '',
      taxType: TaxType.OutputValueAddedTax,
      ratePercent: 10,
      validFrom: dayjs(),
      isValueAddedTaxExempt: false,
      isNotDeclarable: false,
      generalLedgerAccountCode: null,
      legalBasis: null,
    });
    setIsCreateOpen(true);
  }

  function openDescriptionForm(rate: TaxRate): void {
    setEditingDescription(rate);
    descriptionForm.setFieldsValue({
      name: rate.name,
      generalLedgerAccountCode: rate.generalLedgerAccountCode,
      legalBasis: rate.legalBasis,
    });
  }

  function openBehaviourForm(rate: TaxRate): void {
    setEditingBehaviour(rate);
    behaviourForm.setFieldsValue({
      ratePercent: rate.ratePercent,
      isValueAddedTaxExempt: rate.isValueAddedTaxExempt,
      isNotDeclarable: rate.isNotDeclarable,
    });
  }

  function openSupersedeForm(rate: TaxRate): void {
    setSupersedingRate(rate);
    supersedeForm.setFieldsValue({
      // Mặc định là ngày mai: ngày hôm nay đã có thuế suất hiện tại áp dụng, và đổi ngay trong ngày
      // sẽ làm chứng từ lập sáng nay và chiều nay dùng hai tỷ lệ khác nhau.
      effectiveFrom: dayjs().add(1, 'day'),
      ratePercent: rate.ratePercent,
      isValueAddedTaxExempt: rate.isValueAddedTaxExempt,
      isNotDeclarable: rate.isNotDeclarable,
      legalBasis: null,
    });
  }

  const columns: TableColumnsType<TaxRate> = [
    {
      title: 'Mã',
      dataIndex: 'code',
      width: 150,
      render: (code: string) => <Typography.Text strong>{code}</Typography.Text>,
    },
    { title: 'Tên', dataIndex: 'name' },
    {
      title: 'Loại thuế',
      dataIndex: 'taxType',
      width: 170,
      render: (taxType: TaxTypeValue) => taxTypeNames[taxType],
    },
    {
      title: 'Tỷ lệ',
      dataIndex: 'ratePercent',
      width: 90,
      align: 'right',
      render: (ratePercent: number, rate: TaxRate) =>
        rate.isNotDeclarable
          ? '—'
          : `${ratePercent.toLocaleString('vi-VN')}%`,
    },
    {
      title: 'Hiệu lực',
      key: 'validity',
      width: 210,
      render: (_value: unknown, rate: TaxRate) => (
        <Space size={4} wrap>
          <span>
            {dayjs(rate.validFrom).format(dateFormat)}
            {rate.validTo === null ? ' →' : ` – ${dayjs(rate.validTo).format(dateFormat)}`}
          </span>
          {rate.isEffectiveToday && <Tag color="green">Đang áp dụng</Tag>}
        </Space>
      ),
    },
    {
      title: 'TK',
      dataIndex: 'generalLedgerAccountCode',
      width: 70,
      render: (accountCode: string | null) => accountCode ?? '—',
    },
    {
      title: 'Căn cứ pháp lý',
      dataIndex: 'legalBasis',
      width: 200,
      render: (legalBasis: string | null) =>
        legalBasis ?? (
          <Tooltip title="Chưa khai căn cứ pháp lý. Cần có để giải trình với cơ quan thuế.">
            <Typography.Text type="warning">Chưa khai</Typography.Text>
          </Tooltip>
        ),
    },
    {
      title: 'Đánh dấu',
      key: 'flags',
      width: 170,
      render: (_value: unknown, rate: TaxRate) => (
        <Space size={4} wrap>
          {rate.isDefault && <Tag color="blue">Mặc định</Tag>}
          {rate.isValueAddedTaxExempt && <Tag color="orange">Không chịu thuế</Tag>}
          {rate.isNotDeclarable && <Tag>Không phải khai</Tag>}
          {!rate.isActive && <Tag>Đã ngừng</Tag>}
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 260,
      render: (_value: unknown, rate: TaxRate) =>
        canManage ? (
          <Space size={4} wrap>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                openDescriptionForm(rate);
              }}
            >
              Sửa mô tả
            </Button>

            {/*
              Nút chính của màn hình này. Chỉ hiện với dòng còn hiệu lực vô thời hạn — dòng đã có ngày
              hết hiệu lực thì không phải dòng đang áp dụng, và đổi tỷ lệ trên nó là vô nghĩa.
            */}
            {rate.isActive && rate.validTo === null && (
              <Button
                size="small"
                type="primary"
                icon={<CalendarOutlined />}
                onClick={() => {
                  openSupersedeForm(rate);
                }}
              >
                Đổi tỷ lệ từ ngày…
              </Button>
            )}

            {/*
              Sửa tỷ lệ tại chỗ chỉ hiện với dòng CHƯA bắt đầu có hiệu lực — lúc đó nó chưa được dùng
              trên chứng từ nào nên sửa là vô hại. Dấu hiệu này do máy chủ tính theo múi giờ nghiệp vụ,
              giao diện không tự suy ra từ ngày của máy người dùng.
            */}
            {rate.canChangeRateInPlace && (
              <Button
                size="small"
                onClick={() => {
                  openBehaviourForm(rate);
                }}
              >
                Sửa tỷ lệ
              </Button>
            )}

            {!rate.isDefault && rate.isActive && (
              <Button
                size="small"
                onClick={() => {
                  void run(async () => setTaxRateDefault(rate.id), 'Đã đặt làm mặc định.');
                }}
              >
                Đặt mặc định
              </Button>
            )}

            <Button
              size="small"
              onClick={() => {
                void run(
                  async () => setTaxRateActive(rate.id, !rate.isActive),
                  rate.isActive ? 'Đã ngừng dùng.' : 'Đã cho dùng lại.',
                );
              }}
            >
              {rate.isActive ? 'Ngừng' : 'Dùng lại'}
            </Button>
          </Space>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        Thuế suất
      </Typography.Title>

      {errorMessage !== null && <Alert type="error" showIcon message={errorMessage} />}

      <Alert
        type="warning"
        showIcon
        message="Các tỷ lệ nạp sẵn là giá trị khởi tạo cần đối chiếu"
        description={
          'Hệ thống nạp sẵn một bộ thuế suất để bắt đầu làm việc được. Đó KHÔNG phải khẳng định về '
          + 'nghĩa vụ thuế: tỷ lệ và điều kiện áp dụng phụ thuộc vào nội dung từng hợp đồng và vào '
          + 'ngành hàng. Hãy để kế toán hoặc tư vấn thuế xác nhận trước khi lập chứng từ thật, và '
          + 'khai căn cứ pháp lý cho từng dòng — đó là thứ cần khi giải trình với cơ quan thuế.'
        }
      />

      <Alert
        type="info"
        showIcon
        message="Đổi tỷ lệ thì dùng nút “Đổi tỷ lệ từ ngày…”, không sửa tại chỗ"
        description={
          'Thuế suất có hiệu lực theo thời gian: cùng một mã có nhiều dòng qua các giai đoạn. '
          + 'Nút đó đóng khoảng hiệu lực của dòng hiện tại và khai dòng mới, nên tra lại được tỷ lệ '
          + 'nào đã áp dụng khi nào. Sửa tỷ lệ tại chỗ chỉ làm được với dòng chưa bắt đầu có hiệu lực.'
        }
      />

      <Space wrap>
        <Input.Search
          placeholder="Tìm theo mã hoặc tên"
          allowClear
          style={{ width: 240 }}
          onSearch={(value) => {
            setPageNumber(1);
            setSearchTerm(value);
          }}
        />

        <Select<TaxTypeValue | null>
          placeholder="Mọi loại thuế"
          allowClear
          style={{ width: 210 }}
          value={typeFilter}
          options={taxTypeOptions}
          onChange={(value) => {
            setPageNumber(1);
            setTypeFilter(value ?? null);
          }}
        />

        <Space size={4}>
          <Switch
            checked={onlyEffectiveToday}
            onChange={(checked) => {
              setPageNumber(1);
              setOnlyEffectiveToday(checked);
            }}
          />
          <Typography.Text>Chỉ dòng đang áp dụng</Typography.Text>
        </Space>

        <Space size={4}>
          <Switch
            checked={includeInactive}
            onChange={(checked) => {
              setPageNumber(1);
              setIncludeInactive(checked);
            }}
          />
          <Typography.Text>Hiện cả dòng đã ngừng</Typography.Text>
        </Space>

        <Button icon={<ReloadOutlined />} onClick={reload}>
          Tải lại
        </Button>

        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateForm}>
            Khai thuế suất
          </Button>
        )}
      </Space>

      <Table<TaxRate>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={[...items]}
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: pageNumber,
          pageSize,
          total: totalCount,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `${range[0].toLocaleString('vi-VN')}–${range[1].toLocaleString('vi-VN')}`
            + ` trong ${total.toLocaleString('vi-VN')}`,
          onChange: setPageNumber,
        }}
      />

      <Modal
        title="Khai thuế suất mới"
        open={isCreateOpen}
        confirmLoading={isSaving}
        onOk={() => {
          createForm.submit();
        }}
        onCancel={() => {
          setIsCreateOpen(false);
        }}
        okText="Lưu"
        cancelText="Hủy"
        destroyOnHidden
      >
        <Form<CreateFormValues>
          form={createForm}
          layout="vertical"
          requiredMark={false}
          disabled={isSaving}
          onFinish={(values) => {
            void (async () => {
              const succeeded = await run(
                async () =>
                  createTaxRate({
                    code: values.code,
                    name: values.name,
                    taxType: values.taxType,
                    ratePercent: values.ratePercent,
                    validFrom: values.validFrom.format('YYYY-MM-DD'),
                    isValueAddedTaxExempt: values.isValueAddedTaxExempt,
                    isNotDeclarable: values.isNotDeclarable,
                    generalLedgerAccountCode: values.generalLedgerAccountCode,
                    legalBasis: values.legalBasis,
                  }),
                'Đã khai thuế suất.',
              );

              if (succeeded) {
                setIsCreateOpen(false);
              }
            })();
          }}
        >
          <Form.Item
            name="code"
            label="Mã thuế suất"
            extra="Chữ in hoa, chữ số và dấu gạch nối. Ví dụ: VAT-OUT-10, IMPORT-5."
            rules={[{ required: true, message: 'Chưa nhập mã thuế suất.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="name"
            label="Tên thuế suất"
            rules={[{ required: true, message: 'Chưa nhập tên thuế suất.' }]}
          >
            <Input placeholder="VAT đầu ra 10%" />
          </Form.Item>

          <Form.Item
            name="taxType"
            label="Loại thuế"
            extra="Không sửa được sau khi khai: mỗi loại đi vào một chỗ khác nhau trên tờ khai."
            rules={[{ required: true, message: 'Chưa chọn loại thuế.' }]}
          >
            <Select options={taxTypeOptions} />
          </Form.Item>

          <Form.Item
            name="ratePercent"
            label="Tỷ lệ (%)"
            extra="Nhập đúng con số thấy trên hóa đơn: 10 nghĩa là 10 phần trăm."
            rules={[{ required: true, message: 'Chưa nhập tỷ lệ.' }]}
          >
            <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="validFrom"
            label="Có hiệu lực từ ngày"
            extra="Chứng từ lập từ ngày này trở đi sẽ dùng tỷ lệ này."
            rules={[{ required: true, message: 'Chưa chọn ngày bắt đầu hiệu lực.' }]}
          >
            <DatePicker format={dateFormat} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="isValueAddedTaxExempt" valuePropName="checked">
            <Checkbox>
              Không chịu thuế giá trị gia tăng
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Khác với thuế suất 0%. Hàng xuất khẩu chịu thuế suất 0% và vẫn được khấu trừ thuế đầu
                vào; hàng không chịu thuế thì không. Chọn ô này thì tỷ lệ phải để 0.
              </Typography.Paragraph>
            </Checkbox>
          </Form.Item>

          <Form.Item name="isNotDeclarable" valuePropName="checked">
            <Checkbox>Không phải khai và không phải tính thuế</Checkbox>
          </Form.Item>

          <Form.Item
            name="generalLedgerAccountCode"
            label="Tài khoản hạch toán"
            extra="Chỉ gồm chữ số. Ví dụ: 3331 cho VAT đầu ra, 1331 cho VAT đầu vào được khấu trừ."
          >
            <Input placeholder="3331" />
          </Form.Item>

          <Form.Item
            name="legalBasis"
            label="Căn cứ pháp lý"
            extra="Số hiệu văn bản quy định tỷ lệ này. Cần khi giải trình với cơ quan thuế."
          >
            <Input placeholder="Nghị định 180/2024/NĐ-CP" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          editingDescription === null
            ? 'Sửa mô tả'
            : `Sửa mô tả — ${editingDescription.code}`
        }
        open={editingDescription !== null}
        confirmLoading={isSaving}
        onOk={() => {
          descriptionForm.submit();
        }}
        onCancel={() => {
          setEditingDescription(null);
        }}
        okText="Lưu"
        cancelText="Hủy"
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Không đổi tỷ lệ ở đây"
          description={
            'Biểu mẫu này chỉ sửa những thứ không đổi số thuế. Đổi tỷ lệ thì dùng nút '
            + '“Đổi tỷ lệ từ ngày…” trên dòng tương ứng.'
          }
        />

        <Form<DescriptionFormValues>
          form={descriptionForm}
          layout="vertical"
          requiredMark={false}
          disabled={isSaving}
          onFinish={(values) => {
            void (async () => {
              if (editingDescription === null) {
                return;
              }

              const succeeded = await run(
                async () =>
                  updateTaxRateDescription(editingDescription.id, {
                    name: values.name,
                    generalLedgerAccountCode: values.generalLedgerAccountCode,
                    legalBasis: values.legalBasis,
                  }),
                'Đã lưu thay đổi.',
              );

              if (succeeded) {
                setEditingDescription(null);
              }
            })();
          }}
        >
          <Form.Item
            name="name"
            label="Tên thuế suất"
            rules={[{ required: true, message: 'Chưa nhập tên thuế suất.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="generalLedgerAccountCode" label="Tài khoản hạch toán">
            <Input placeholder="3331" />
          </Form.Item>

          <Form.Item
            name="legalBasis"
            label="Căn cứ pháp lý"
            extra="Bổ sung căn cứ cho một dòng đang dùng là việc bình thường và cần thiết."
          >
            <Input placeholder="Nghị định 180/2024/NĐ-CP" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          editingBehaviour === null
            ? 'Sửa tỷ lệ'
            : `Sửa tỷ lệ — ${editingBehaviour.code}`
        }
        open={editingBehaviour !== null}
        confirmLoading={isSaving}
        onOk={() => {
          behaviourForm.submit();
        }}
        onCancel={() => {
          setEditingBehaviour(null);
        }}
        okText="Lưu"
        cancelText="Hủy"
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Dòng này chưa bắt đầu có hiệu lực"
          description={
            'Vì vậy sửa tỷ lệ tại chỗ được: nó chưa được dùng trên chứng từ nào. Từ ngày nó bắt đầu '
            + 'có hiệu lực thì chỉ đổi được bằng nút “Đổi tỷ lệ từ ngày…”.'
          }
        />

        <Form<BehaviourFormValues>
          form={behaviourForm}
          layout="vertical"
          requiredMark={false}
          disabled={isSaving}
          onFinish={(values) => {
            void (async () => {
              if (editingBehaviour === null) {
                return;
              }

              const succeeded = await run(
                async () =>
                  changeTaxRateBehaviour(editingBehaviour.id, {
                    ratePercent: values.ratePercent,
                    isValueAddedTaxExempt: values.isValueAddedTaxExempt,
                    isNotDeclarable: values.isNotDeclarable,
                  }),
                'Đã sửa tỷ lệ.',
              );

              if (succeeded) {
                setEditingBehaviour(null);
              }
            })();
          }}
        >
          <Form.Item
            name="ratePercent"
            label="Tỷ lệ (%)"
            rules={[{ required: true, message: 'Chưa nhập tỷ lệ.' }]}
          >
            <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="isValueAddedTaxExempt" valuePropName="checked">
            <Checkbox>Không chịu thuế giá trị gia tăng (tỷ lệ phải để 0)</Checkbox>
          </Form.Item>

          <Form.Item name="isNotDeclarable" valuePropName="checked">
            <Checkbox>Không phải khai và không phải tính thuế</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          supersedingRate === null
            ? 'Đổi tỷ lệ từ ngày'
            : `Đổi tỷ lệ — ${supersedingRate.code}`
        }
        open={supersedingRate !== null}
        confirmLoading={isSaving}
        onOk={() => {
          supersedeForm.submit();
        }}
        onCancel={() => {
          setSupersedingRate(null);
        }}
        okText="Đổi tỷ lệ"
        cancelText="Hủy"
        destroyOnHidden
      >
        {supersedingRate !== null && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Tỷ lệ hiện tại: ${supersedingRate.ratePercent.toLocaleString('vi-VN')}%`}
            description={
              'Dòng hiện tại sẽ được đóng vào ngày liền trước ngày bạn chọn, và một dòng mới được '
              + 'khai từ ngày đó. Chứng từ cũ vẫn tra ra được tỷ lệ đã dùng.'
            }
          />
        )}

        <Form<SupersedeFormValues>
          form={supersedeForm}
          layout="vertical"
          requiredMark={false}
          disabled={isSaving}
          onFinish={(values) => {
            void (async () => {
              if (supersedingRate === null) {
                return;
              }

              const succeeded = await run(
                async () =>
                  supersedeTaxRate(supersedingRate.id, {
                    effectiveFrom: values.effectiveFrom.format('YYYY-MM-DD'),
                    ratePercent: values.ratePercent,
                    isValueAddedTaxExempt: values.isValueAddedTaxExempt,
                    isNotDeclarable: values.isNotDeclarable,
                    legalBasis: values.legalBasis,
                  }),
                'Đã đổi tỷ lệ.',
              );

              if (succeeded) {
                setSupersedingRate(null);
              }
            })();
          }}
        >
          <Form.Item
            name="effectiveFrom"
            label="Tỷ lệ mới áp dụng từ ngày"
            extra="Phải sau ngày bắt đầu hiệu lực của dòng hiện tại."
            rules={[{ required: true, message: 'Chưa chọn ngày áp dụng.' }]}
          >
            <DatePicker format={dateFormat} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="ratePercent"
            label="Tỷ lệ mới (%)"
            rules={[{ required: true, message: 'Chưa nhập tỷ lệ mới.' }]}
          >
            <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="isValueAddedTaxExempt" valuePropName="checked">
            <Checkbox>Không chịu thuế giá trị gia tăng (tỷ lệ phải để 0)</Checkbox>
          </Form.Item>

          <Form.Item name="isNotDeclarable" valuePropName="checked">
            <Checkbox>Không phải khai và không phải tính thuế</Checkbox>
          </Form.Item>

          <Form.Item
            name="legalBasis"
            label="Căn cứ pháp lý của tỷ lệ mới"
            extra="Tỷ lệ mới bao giờ cũng đến từ một văn bản mới. Khai ngay để sau này không phải tra lại."
          >
            <Input placeholder="Nghị định 180/2024/NĐ-CP" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
